import React, { useState, Fragment, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import Collapse from "@mui/material/Collapse";
import makeStyles from '@mui/styles/makeStyles';
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import COLORS from "shared/constants/colors";
import { getDrawerItems } from "components/Drawer/data";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import InfoIcon from "@mui/icons-material/Info";
import AboutDialog from "components/Drawer/AboutModal";
import {
  Logo,
  ItemText,
  Blinking,
  LogoWrapper,
  DrawerContainer,
  NavDivider,
  NewBadge,
} from "styles/components/Drawer";

const useStyles = makeStyles({
  root: {
    "& .MuiPaper-root": {
      backgroundColor: "#e5e5e5",
    },
  },
  wrapper: {
    width: 300,
    backgroundColor: "#ffffff",
    color: COLORS.SKY_BLUE,
    borderRadius: "12px",
    margin: "0 15px 15px",
    padding: "8px 10px",
    boxShadow: "0 1px 8px rgba(0, 0, 0, 0.05)",
  },
  navItem: {
    display: "block",
    width: "100%",
    textDecoration: "none",
    color: "inherit",
    borderRadius: "8px",
    marginBottom: "2px",
    "&:last-child": {
      marginBottom: 0,
    },
    "&:hover .MuiListItemButton-root": {
      backgroundColor: "#eef7fc",
      color: "#18191F",
      "& .MuiListItemIcon-root": {
        color: "#1594db",
      },
    },
  },
  items: {
    color: "#18191F",
    minHeight: "42px",
    padding: "9px 12px",
    width: "100%",
    margin: 0,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "background-color 0.15s ease, color 0.15s ease",
    "& .MuiListItemIcon-root": {
      color: "#1594db",
      minWidth: "22px",
      marginRight: 0,
      "& .MuiSvgIcon-root, & svg": {
        color: "inherit",
        fontSize: "20px",
        width: "20px",
        height: "20px",
      },
    },
  },
  icon: {
    color: "inherit",
    minWidth: "22px",
    marginRight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  innerList: {
    minHeight: "36px",
    padding: "6px 12px 6px 28px",
    color: "#fff",
    width: "100%",
    margin: 0,
    borderRadius: "6px",
    "&:hover": {
      backgroundColor: "#0273b2",
      color: "#fff",
    },
  },
  activeLink: {
    "& .MuiListItemButton-root": {
      backgroundColor: "#1594DB",
      color: "#fff",
      "& .MuiListItemIcon-root": {
        color: "#fff",
      },
    },
    "&:hover .MuiListItemButton-root": {
      backgroundColor: "#1288c7",
      color: "#fff",
      "& .MuiListItemIcon-root": {
        color: "#fff",
      },
    },
  },
  activeChildLink: {
    "& .MuiListItemButton-root": {
      backgroundColor: "#0273b2",
      color: "#fff",
    },
  },
  dividerWrap: {
    padding: "6px 0",
    marginBottom: "2px",
  },
});

const CustomDrawer = ({ open, toggle }) => {
  const [collapseVisibility, updateCollapseVisibility] = useState({});
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const location = useLocation();
  const classes = useStyles();
  const drawerItems = getDrawerItems(emailid);

  useEffect(() => {
    const items = getDrawerItems(emailid);
    const expandedParents = items.reduce((acc, item) => {
      if (
        item.childItems?.some((childItem) => childItem.route === location.pathname)
      ) {
        acc[item.title] = true;
      }
      return acc;
    }, {});

    if (Object.keys(expandedParents).length) {
      updateCollapseVisibility((state) => {
        const hasChange = Object.keys(expandedParents).some((key) => !state[key]);
        return hasChange ? { ...state, ...expandedParents } : state;
      });
    }
  }, [location.pathname, emailid]);

  const homeWebPage = useSelector((state) => state?.homeWebPage);
  const homeData = homeWebPage?.find((p) => p?.name === "Header") || {};
  const { components } = homeData;
  const { Image } = components || {};

  const getNavLinkClassName = (isChild) => ({ isActive }) =>
    [
      classes.navItem,
      isActive
        ? isChild
          ? classes.activeChildLink
          : classes.activeLink
        : null,
    ]
      .filter(Boolean)
      .join(" ");

  const CustomListItem = ({ item, isChild }) => (
    <ListItemButton
      className={isChild ? classes.innerList : classes.items}
      onClick={() => {
        if (item.route) {
          toggle();
          return;
        }
        updateCollapseVisibility({
          ...collapseVisibility,
          [item.title]: !collapseVisibility[item.title],
        });
      }}
    >
      {item.icon ? (
        <ListItemIcon className={classes.icon}>
          {React.isValidElement(item.icon)
            ? React.cloneElement(item.icon, { color: "inherit" })
            : item.icon}
        </ListItemIcon>
      ) : null}
      <ItemText>
        {item.title}
        {item?.inventory && (
          <>
            &nbsp;&nbsp;[&nbsp;
            <Blinking>
              <circle cx="5" cy="5" r="5" fill="red" />
            </Blinking>
            &nbsp;&nbsp;LIVE&nbsp;]
          </>
        )}
      </ItemText>
      {item?.newRelease && (
        <NewBadge>
          <NewReleasesIcon />
          New
        </NewBadge>
      )}
    </ListItemButton>
  );

  return (
    <Drawer anchor="left" open={open} onClose={toggle} className={classes.root}>
      <DrawerContainer>
        <LogoWrapper>
          <Logo src={Image} alt="DrawerLogo" />
        </LogoWrapper>
        <List className={classes.wrapper} disablePadding>
          {drawerItems.map((item, index) =>
            item.divider ? (
              <div key={`divider_${index}`} className={classes.dividerWrap}>
                <NavDivider />
              </div>
            ) : item.route ? (
              <NavLink
                to={item.route}
                end
                key={`${item.title}_link_${index}`}
                className={getNavLinkClassName(false)}
              >
                <CustomListItem item={item} />
              </NavLink>
            ) : (
              <Fragment key={`${item.title}_${index}`}>
                <div className={classes.navItem}>
                  <CustomListItem item={item} />
                </div>
                <Collapse in={collapseVisibility[item.title]}>
                  {item.childItems.map((childItem, childIndex) =>
                    childItem.route ? (
                      <NavLink
                        to={childItem.route}
                        end
                        className={getNavLinkClassName(true)}
                        key={`${item.title}_childLink_${childIndex}`}
                      >
                        <CustomListItem
                          isChild={true}
                          item={childItem}
                          key={`${item.title}_childLinkItem_${childIndex}`}
                        />
                      </NavLink>
                    ) : (
                      <CustomListItem
                        item={childItem}
                        key={`${item.title}_childItem_${childIndex}`}
                      />
                    )
                  )}
                </Collapse>
              </Fragment>
            )
          )}
          <div className={classes.navItem}>
            <ListItemButton
              className={classes.items}
              onClick={() => setAboutModalOpen(!aboutModalOpen)}
            >
              <ListItemIcon className={classes.icon}>
                <InfoIcon />
              </ListItemIcon>
              <ItemText>About Us</ItemText>
            </ListItemButton>
          </div>
        </List>
        <AboutDialog
          open={aboutModalOpen}
          setOpen={(v) => setAboutModalOpen(v)}
        />
      </DrawerContainer>
    </Drawer>
  );
};

CustomDrawer.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
};

export default CustomDrawer;
